'use client';

import React, { useState, useRef, useEffect } from 'react';

const DIENSTEN_OPTIONS = [
  { group: 'Problemen & Symptomen', items: [
    'Roetfilter lampje brandt',
    'Auto in noodloopmodus',
    'Afgekeurd bij keuring',
    'Deeltjestest niet geslaagd',
    'Vermogensverlies',
    'Hoger brandstofverbruik',
    'Ander probleem',
  ]},
  { group: 'Diensten', items: [
    'Diagnose & emissietest',
    'Roetfilter reinigen',
    'Roetfilter revisie',
    'Roetfilter vervangen',
    'Montage, demontage + programmeren',
  ]},
];

interface MultiSelectDienstProps {
  value: string[];
  onChange: (selected: string[]) => void;
  className?: string;
  inputClassName?: string;
  required?: boolean;
}

export default function MultiSelectDienst({ value, onChange, className = '', inputClassName = '', required = false }: MultiSelectDienstProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (item: string) => {
    if (value.includes(item)) {
      onChange(value.filter((v) => v !== item));
    } else {
      onChange([...value, item]);
    }
  };

  const displayText = value.length === 0
    ? 'Selecteer een probleem of dienst'
    : value.length === 1
    ? value[0]
    : `${value.length} geselecteerd`;

  const isPlaceholder = value.length === 0;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Hidden input for required validation */}
      {required && (
        <input
          type="text"
          required
          value={value.length > 0 ? value[0] : ''}
          onChange={() => {}}
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
        />
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full text-left flex items-center justify-between gap-2 border border-border rounded-xl px-4 bg-white focus:outline-none focus:ring-2 focus:ring-primary transition-all cursor-pointer ${inputClassName} ${open ? 'ring-2 ring-primary border-primary' : ''}`}
      >
        <span className={`truncate ${isPlaceholder ? 'text-gray-400' : 'text-foreground font-medium'}`}>
          {displayText}
        </span>
        <svg
          className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Selected tags (shown below trigger when multiple selected) */}
      {value.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full"
            >
              {v}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggle(v); }}
                className="hover:text-primary/70 transition-colors ml-0.5"
                aria-label={`Verwijder ${v}`}
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown panel */}
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-border rounded-xl shadow-lg overflow-hidden">
          <div className="max-h-72 overflow-y-auto py-1">
            {DIENSTEN_OPTIONS.map((group) => (
              <div key={group.group}>
                <div className="px-4 py-2 text-xs font-bold text-muted-foreground uppercase tracking-wider bg-secondary/50 border-b border-border">
                  {group.group}
                </div>
                {group.items.map((item) => {
                  const selected = value.includes(item);
                  return (
                    <button
                      key={item}
                      type="button"
                      onClick={() => toggle(item)}
                      className={`w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-secondary/60 ${selected ? 'bg-primary/5 text-primary font-semibold' : 'text-foreground'}`}
                    >
                      <span className={`w-4 h-4 shrink-0 rounded border-2 flex items-center justify-center transition-colors ${selected ? 'bg-primary border-primary' : 'border-gray-300 bg-white'}`}>
                        {selected && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      {item}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          {value.length > 0 && (
            <div className="border-t border-border px-4 py-2.5 flex items-center justify-between bg-secondary/30">
              <span className="text-xs text-muted-foreground">{value.length} geselecteerd</span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-primary font-semibold hover:underline"
              >
                Wis selectie
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
